import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ChevronLeftIcon,
  ExitIcon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  HamburgerMenuIcon,
  HomeIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import { BrandMark } from './BrandMark';
import { toggleWorkspaceFullscreen } from '../services/workspace-fullscreen';

const PREFERENCE_KEY = 'wx-weiqi:teacher-home:auto-hide';
const COMPACT_QUERY = '(max-width: 760px)';

function readAutoHide(): boolean {
  try {
    return localStorage.getItem(PREFERENCE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Local presentation preference only; opening the rail never changes the content width. */
export function TeacherHomeNavigation({
  children,
  displayName,
  onLogout,
  onHome,
  currentPage = 'home',
}: {
  readonly children: ReactNode | ((closeNavigation: () => void) => ReactNode);
  readonly displayName: string;
  readonly onLogout?: (() => void) | undefined;
  readonly onHome?: (() => void) | undefined;
  readonly currentPage?: 'home' | 'textbooks';
}) {
  const [autoHide, setAutoHide] = useState(readAutoHide);
  const [open, setOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenPending, setFullscreenPending] = useState(false);
  const [fullscreenError, setFullscreenError] = useState<string | null>(null);
  const fullscreenRequest = useRef(false);
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(COMPACT_QUERY).matches === true,
  );
  const rail = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pointerInside = useRef(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expanded = (!compact && !autoHide) || open;
  const cancelHide = () => {
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  const scheduleHide = () => {
    cancelHide();
    // Compact navigation is an explicit drawer; it stays open until dismissed.
    if (compact) return;
    hideTimer.current = setTimeout(() => {
      if (!pointerInside.current && !rail.current?.contains(document.activeElement)) setOpen(false);
    }, 450);
  };
  const dismiss = () => {
    cancelHide();
    trigger.current?.focus();
    setOpen(false);
  };

  useEffect(() => {
    if (!window.matchMedia) return;
    const query = window.matchMedia(COMPACT_QUERY);
    const update = () => {
      setCompact(query.matches);
      setOpen(false);
    };
    query.addEventListener('change', update);
    return () => {
      query.removeEventListener('change', update);
      if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    };
  }, []);

  useEffect(() => {
    const sync = () => {
      setFullscreen(document.fullscreenElement === document.documentElement);
      setFullscreenError(null);
    };
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFullscreen = async () => {
    if (fullscreenRequest.current) return;
    fullscreenRequest.current = true;
    setFullscreenPending(true);
    setFullscreenError(null);
    try {
      setFullscreenError(await toggleWorkspaceFullscreen(document.documentElement, document));
      setFullscreen(document.fullscreenElement === document.documentElement);
    } finally {
      fullscreenRequest.current = false;
      setFullscreenPending(false);
    }
  };

  return (
    <div
      className={`teacher-home-nav${autoHide ? ' teacher-home-nav--auto' : ''}${expanded ? ' is-expanded' : ''}`}
    >
      <button
        ref={trigger}
        className="teacher-home-nav__trigger"
        type="button"
        aria-label="展开导航"
        aria-expanded={expanded}
        aria-controls="teacher-home-navigation"
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse' && !compact) {
            cancelHide();
            setOpen(true);
          }
        }}
        onPointerLeave={scheduleHide}
        onClick={() => {
          cancelHide();
          setOpen(true);
        }}
      >
        <HamburgerMenuIcon aria-hidden="true" />
        <span>导航</span>
      </button>
      {compact && expanded ? (
        <button
          className="teacher-home-nav__backdrop"
          type="button"
          aria-label="收起导航遮罩"
          onClick={dismiss}
        />
      ) : null}
      <div
        ref={rail}
        id="teacher-home-navigation"
        className="teacher-home-nav__rail"
        inert={!expanded}
        onPointerEnter={() => {
          pointerInside.current = true;
          cancelHide();
        }}
        onPointerLeave={() => {
          pointerInside.current = false;
          scheduleHide();
        }}
        onFocus={cancelHide}
        onBlur={scheduleHide}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && (autoHide || compact)) {
            event.preventDefault();
            dismiss();
          }
        }}
      >
        <a
          className="teacher-home-nav__brand"
          href="#teacher-home-heading"
          aria-label="弈时围棋教师工作台"
          onClick={(event) => {
            if (!onHome) return;
            event.preventDefault();
            setOpen(false);
            onHome();
          }}
        >
          <BrandMark />
          <span>
            <strong>弈时围棋</strong>
            <small>教师端</small>
          </span>
        </a>
        <nav aria-label={currentPage === 'home' ? '教师首页导航' : '教师教材导航'}>
          <p className="teacher-home-nav__caption">教学工作台</p>
          <a
            className="teacher-home-nav__home"
            href="#teacher-home-heading"
            aria-current={currentPage === 'home' ? 'page' : undefined}
            onClick={(event) => {
              setOpen(false);
              if (onHome) {
                event.preventDefault();
                onHome();
              } else {
                document.getElementById('teacher-home-heading')?.focus();
              }
            }}
          >
            <HomeIcon aria-hidden="true" />
            <span>工作台</span>
          </a>
          {typeof children === 'function'
            ? children(() => {
                cancelHide();
                setOpen(false);
              })
            : children}
        </nav>
        <div className="teacher-home-nav__footer">
          <button
            className="teacher-home-nav__fullscreen"
            type="button"
            disabled={fullscreenPending}
            aria-busy={fullscreenPending}
            onClick={toggleFullscreen}
          >
            {fullscreen ? (
              <ExitFullScreenIcon aria-hidden="true" />
            ) : (
              <EnterFullScreenIcon aria-hidden="true" />
            )}
            <span>{fullscreen ? '退出全屏' : '进入全屏'}</span>
          </button>
          {fullscreenError ? (
            <p className="teacher-home-nav__fullscreen-error" role="alert">
              {fullscreenError}
            </p>
          ) : null}
          <button
            className="teacher-home-nav__auto-hide"
            type="button"
            role="switch"
            aria-checked={autoHide}
            aria-label="自动隐藏导航"
            onClick={(event) => {
              const next = !autoHide;
              setAutoHide(next);
              setOpen(true);
              // A mouse click must not pin the rail through focus; keyboard toggles keep focus.
              if (next && (event?.detail ?? 0) > 0) event.currentTarget.blur();
              try {
                localStorage.setItem(PREFERENCE_KEY, String(next));
              } catch {
                /* Preference remains usable for this visit. */
              }
            }}
          >
            <HamburgerMenuIcon aria-hidden="true" />
            <span>自动隐藏</span>
            <span className="teacher-home-nav__switch" aria-hidden="true" />
          </button>
          <div className="teacher-home-nav__account">
            <PersonIcon aria-hidden="true" />
            <strong title={displayName}>{displayName}</strong>
          </div>
          {onLogout ? (
            <button className="teacher-home-nav__logout" type="button" onClick={onLogout}>
              <ExitIcon aria-hidden="true" />
              退出
            </button>
          ) : null}
          {autoHide || compact ? (
            <button className="teacher-home-nav__close" type="button" onClick={dismiss}>
              <ChevronLeftIcon aria-hidden="true" />
              收起导航
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
